import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatTimeLabel } from "../../../shared/utils/dateTime";
import { sortRegistrationCategories } from "../../challenges/utils/registrationCategoryOrder";
import type { TournamentRegistrationCategoryItem } from "../utils/tournamentCategories";

type TournamentParticipantsSummary = Record<
  string,
  {
    current: number;
    limit: number;
    registrationType: "individual" | "fixed_pair";
  }
>;

type AdminTournamentCategoryTabsProps = {
  activeCategoryKey: string;
  categories: TournamentRegistrationCategoryItem[];
  fixedPairCountsAsPairs?: boolean;
  onAddCategory: () => void;
  onEditCategory: (categoryKey: string) => void;
  onRemoveCategory: (categoryKey: string) => void;
  onSelectCategory: (categoryKey: string) => void;
  participantsSummaryByCategory: TournamentParticipantsSummary;
};

export default function AdminTournamentCategoryTabs({
  activeCategoryKey,
  categories,
  fixedPairCountsAsPairs = false,
  onAddCategory,
  onEditCategory,
  onRemoveCategory,
  onSelectCategory,
  participantsSummaryByCategory,
}: AdminTournamentCategoryTabsProps) {
  const t = siteTexts.admin;
  const orderedCategories = sortRegistrationCategories(categories);
  return (
    <div className="admin-create-tournament-categories">
      <div className="status-filter-buttons admin-category-tab-actions">
        {orderedCategories.map((category) => {
          const key = String(category.categoryKey || "").trim();
          if (!key) return null;
          const summary = participantsSummaryByCategory[key];
          const current = Number(summary?.current || 0);
          const limit = Number(summary?.limit || 0);
          const registrationType =
            category.registrationType === "individual"
              ? "individual"
              : "fixed_pair";
          const modeLabel =
            registrationType === "fixed_pair"
              ? t.tournamentCategoryEditor.fixedPair
              : t.tournamentCategoryEditor.individual;
          const scheduledTime = category.scheduledAt
            ? formatTimeLabel(category.scheduledAt)
            : "";
          const countLabel =
            fixedPairCountsAsPairs && registrationType === "fixed_pair"
              ? `${current}/${limit} duplas`
              : `${current}/${limit}`;
          return (
            <Button
              key={`participants-category-tab-${key}`}
              type="button"
              variant="pill"
              className={activeCategoryKey === key ? "active" : ""}
              onClick={() => onSelectCategory(key)}
            >
              {String(
                category.categoryName ||
                  t.tournamentCategoryEditor.fallbackCategory,
              ).trim()}{" "}
              ({scheduledTime ? `${scheduledTime}, ` : ""}
              {modeLabel}, {countLabel})
            </Button>
          );
        })}
        <Button
          type="button"
          variant="pill"
          className="admin-category-add-button"
          onClick={onAddCategory}
          aria-label={t.tournamentCategoryEditor.addCategory}
          title={t.tournamentCategoryEditor.addCategory}
        >
          +
        </Button>
      </div>
      <div className="admin-active-category-actions">
        <Button
          type="button"
          variant="primary"
          className="admin-category-edit-selected-button"
          disabled={!activeCategoryKey}
          onClick={() => onEditCategory(activeCategoryKey)}
        >
          {t.tournamentCategoryEditor.editSelectedCategory}
        </Button>
        <Button
          type="button"
          variant="danger"
          className="admin-category-remove-button"
          disabled={!activeCategoryKey || categories.length <= 1}
          onClick={() => onRemoveCategory(activeCategoryKey)}
        >
          {t.tournamentCategoryEditor.removeCategory}
        </Button>
      </div>
    </div>
  );
}
